import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationCategory,
  NotificationSubscriptionResponseDto,
  UpdateNotificationSubscriptionsDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useNotificationSubscriptions = (httpClient: AxiosInstance) => {
  const baseUrl = '/notification-subscriptions';
  const queryClient = useQueryClient();

  const getSubscriptions = async (
    category: NotificationCategory,
  ): Promise<NotificationSubscriptionResponseDto> => {
    const { data } = await httpClient.get<{ data: NotificationSubscriptionResponseDto }>(baseUrl, {
      params: { category },
    });
    return data.data;
  };

  const updateSubscriptions = async ({
    category,
    productAreaIds,
  }: {
    category: NotificationCategory;
    productAreaIds: number[];
  }): Promise<NotificationSubscriptionResponseDto> => {
    const { data } = await httpClient.put<{ data: NotificationSubscriptionResponseDto }>(
      baseUrl,
      { productAreaIds },
      { params: { category } },
    );
    return data.data;
  };

  const useGetSubscriptions = (category: NotificationCategory) =>
    useQuery({
      queryKey: [baseUrl, category],
      queryFn: () => getSubscriptions(category),
      enabled: !!category,
    });

  const useUpdateSubscriptions = (category: NotificationCategory) =>
    useErrorHandlingMutation({
      mutationFn: (dto: UpdateNotificationSubscriptionsDto) =>
        updateSubscriptions({ category, ...dto }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [baseUrl, category] });
      },
    });

  return {
    getSubscriptions,
    updateSubscriptions,
    useGetSubscriptions,
    useUpdateSubscriptions,
  };
};
